import type { ComponentProps, CSSProperties, ReactNode } from "react";
import { useMemo, useState } from "react";
import { CButton, CSmartTable } from "@coreui/react-pro";

type SmartTableProps = ComponentProps<typeof CSmartTable>;
import type { TradingStrategyTradeDto } from "../../../api/hooks/useTradingStrategies";
import {
  TradingStrategyTradeDirection,
  tradeDirectionLabel,
} from "../../../api/hooks/useTradingStrategies";

// Must appear in itemsPerPageOptions below. CoreUI's items-per-page control is a plain
// <select> whose defaultValue is the page size, and its default options are [5, 10, 20, 50] -
// a size outside that list matches no option, so the browser shows the first one instead and
// the control reads "5" while the table pages at 25.
const PAGE_SIZE = 25;

// Aligned digits without switching font. The columns need to line up under each other; they do not
// need to look like code, which is what a monospace family reads as next to the rest of the page.
const TABULAR: CSSProperties = { fontVariantNumeric: "tabular-nums" };

// Entry and exit are two pairs of columns, not four. Without the rule between them a right-aligned
// entry price sits flush against a left-aligned exit date and the two read as one figure.
const EXIT_GROUP = "border-start";

const ALL = "";

type ResultFilter = typeof ALL | "winners" | "losers";

interface TradesTableProps {
  trades: TradingStrategyTradeDto[];
}

/**
 * Every trade the backtest took: direction, entry, exit and result, one row each.
 *
 * The strongest evidence on the page — the figures above it are summaries, and this is the record
 * they were summarised from. What it does not show is why any trade was taken: the conditions that
 * open and close one are what a subscription buys.
 */
export function TradesTable({ trades }: TradesTableProps) {
  const [direction, setDirection] = useState(ALL);
  const [result, setResult] = useState<ResultFilter>(ALL);

  // Taken from the data, so a long-only strategy offers no Short pill that answers with nothing.
  const directions = useMemo(
    () =>
      [...new Set(trades.map((trade) => trade.direction))]
        .filter((d) => d !== TradingStrategyTradeDirection.Unknown)
        .sort(),
    [trades],
  );

  const matchesDirection = (trade: TradingStrategyTradeDto) =>
    direction === ALL || String(trade.direction) === direction;

  const matchesResult = (trade: TradingStrategyTradeDto) =>
    result === ALL
      ? true
      : result === "losers"
        ? trade.returnPct < 0
        : trade.returnPct >= 0;

  const filtered = useMemo(
    () =>
      trades.filter((trade) => matchesDirection(trade) && matchesResult(trade)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trades, direction, result],
  );

  // A group's counts have every other selection applied but not its own, so they answer "of what I
  // am looking at, how many are these?" and always add up to that group's own All.
  const directionCounts = useMemo(() => {
    const base = trades.filter(matchesResult);

    return {
      all: base.length,
      byDirection: base.reduce<Record<string, number>>((tally, trade) => {
        const key = String(trade.direction);
        tally[key] = (tally[key] ?? 0) + 1;

        return tally;
      }, {}),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trades, result]);

  const resultCounts = useMemo(() => {
    const base = trades.filter(matchesDirection);

    return {
      all: base.length,
      winners: base.filter((trade) => trade.returnPct >= 0).length,
      losers: base.filter((trade) => trade.returnPct < 0).length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trades, direction]);

  // An FX pair at 1.0842 and an index at 4,183.25 are both wrong forced into the other's shape.
  const priceDecimals = useMemo(() => decimalsFor(trades), [trades]);

  const columns = useMemo<NonNullable<SmartTableProps["columns"]>>(
    () => [
      { key: "tradeNumber", label: "#", _style: { width: "6%" } },
      { key: "direction", label: "Direction", _style: { width: "12%" } },
      { key: "entryDate", label: "Entry date", _style: { width: "16%" } },
      {
        key: "entryPrice",
        label: "Entry price",
        _style: { width: "14%", textAlign: "right" },
      },
      {
        key: "exitDate",
        label: "Exit date",
        _style: {
          width: "16%",
          borderLeft: "var(--cui-border-width) solid var(--cui-border-color)",
        },
      },
      {
        key: "exitPrice",
        label: "Exit price",
        _style: { width: "14%", textAlign: "right" },
      },
      // Sorts on the number, renders the preformatted string in the scoped column below. Keyed on
      // returnValue the sorter compared "+8.8%" against "+55.3%" as text and put the 8 above the 55.
      {
        key: "returnPct",
        label: "Return",
        _style: { width: "14%", textAlign: "right" },
      },
    ],
    [],
  );

  if (trades.length === 0) {
    return (
      <div className="text-body-secondary text-center py-5">
        No trades have been published for this strategy.
      </div>
    );
  }

  return (
    <>
      <div className="d-flex flex-wrap gap-4 mb-3 strategy-trade-filters">
        {directions.length > 1 && (
          <FilterGroup caption="Direction">
            <FilterPill
              label="All"
              count={directionCounts.all}
              active={direction === ALL}
              onClick={() => setDirection(ALL)}
            />
            {directions.map((option) => (
              <FilterPill
                key={option}
                label={tradeDirectionLabel(option)}
                count={directionCounts.byDirection[String(option)] ?? 0}
                active={direction === String(option)}
                onClick={() => setDirection(String(option))}
              />
            ))}
          </FilterGroup>
        )}

        <FilterGroup caption="Result">
          <FilterPill
            label="All"
            count={resultCounts.all}
            active={result === ALL}
            onClick={() => setResult(ALL)}
          />
          {/* Coloured to match the return column, so the pill and the figures it selects agree. */}
          <FilterPill
            label="Winners"
            count={resultCounts.winners}
            active={result === "winners"}
            activeColor="success"
            onClick={() => setResult("winners")}
          />
          <FilterPill
            label="Losers"
            count={resultCounts.losers}
            active={result === "losers"}
            activeColor="danger"
            onClick={() => setResult("losers")}
          />
        </FilterGroup>
      </div>

      {/* Nine columns is the right record on a desktop and unreadable on a phone: at 390px only the
          first four fit, so Return — the column the whole table is scanned for — sits off-screen
          behind a sideways drag. The phone gets a card per trade instead.

          Chosen in CSS rather than from a measured width, so neither layout paints before a
          breakpoint hook has mounted (WI #271). Both are rendered; only one is displayed. */}
      <div className="d-none d-sm-block">
        <CSmartTable
          columns={columns}
          items={filtered as unknown as Record<string, unknown>[]}
          columnSorter
          sorterValue={{ column: "tradeNumber", state: "asc" }}
          pagination
          itemsPerPage={PAGE_SIZE}
          itemsPerPageOptions={[10, 25, 50, 100]}
          itemsPerPageSelect
          noItemsLabel="No trades match these filters."
          tableProps={{ small: true, hover: true, responsive: true }}
          tableBodyProps={{ className: "align-middle" }}
          scopedColumns={{
            tradeNumber: (item: TradingStrategyTradeDto) => (
              <td className="text-end text-body-secondary" style={TABULAR}>
                {item.tradeNumber}
              </td>
            ),
            direction: (item: TradingStrategyTradeDto) => (
              <td>
                <span
                  className={`badge border ${
                    item.direction === TradingStrategyTradeDirection.Short
                      ? "text-bg-secondary"
                      : "text-bg-light"
                  }`}
                >
                  {tradeDirectionLabel(item.direction)}
                </span>
              </td>
            ),
            entryDate: (item: TradingStrategyTradeDto) => (
              <td style={TABULAR}>{formatDate(item.entryDate)}</td>
            ),
            entryPrice: (item: TradingStrategyTradeDto) => (
              <td className="text-end" style={TABULAR}>
                {formatPrice(item.entryPrice, priceDecimals)}
              </td>
            ),
            exitDate: (item: TradingStrategyTradeDto) => (
              <td className={EXIT_GROUP} style={TABULAR}>
                {formatDate(item.exitDate)}
              </td>
            ),
            exitPrice: (item: TradingStrategyTradeDto) => (
              <td className="text-end" style={TABULAR}>
                {formatPrice(item.exitPrice, priceDecimals)}
              </td>
            ),
            returnPct: (item: TradingStrategyTradeDto) => (
              <td
                className={`text-end fw-semibold ${
                  item.returnPct < 0 ? "text-danger" : "text-success"
                }`}
                style={TABULAR}
              >
                {item.returnValue}
              </td>
            ),
          } as unknown as SmartTableProps["scopedColumns"]}
        />
      </div>

      <div className="d-sm-none">
        {/* Keyed on the filters so a narrowed list starts at the top: remounting resets how many
            cards are revealed, which a reader 40 cards deep would otherwise keep. */}
        <TradeCards
          key={`${direction}|${result}`}
          trades={filtered}
          priceDecimals={priceDecimals}
        />
      </div>
    </>
  );
}

/**
 * The phone layout: one card per trade, ordered as the backtest took them.
 *
 * Return leads the card because it is what the list is scanned for, and entry and exit sit under it
 * as a labelled pair rather than four columns — the same grouping the desktop rule between them
 * makes, expressed as two rows instead.
 *
 * No column sorting here, unlike the desktop table. A phone reader is scanning evidence rather than
 * analysing it, and the Winners/Losers pills already answer the question sorting by return would.
 */
function TradeCards({
  trades,
  priceDecimals,
}: {
  trades: TradingStrategyTradeDto[];
  priceDecimals: number;
}) {
  const [visible, setVisible] = useState(PAGE_SIZE);

  if (trades.length === 0) {
    return (
      <div className="text-body-secondary text-center py-5">
        No trades match these filters.
      </div>
    );
  }

  const shown = trades.slice(0, visible);
  const remaining = trades.length - shown.length;

  return (
    <>
      <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
        {shown.map((trade) => (
          <li key={trade.tradeNumber} className="card trade-card">
            <div className="card-body p-3">
              <div className="d-flex align-items-center gap-2 mb-2">
                <span className="text-body-secondary" style={TABULAR}>
                  #{trade.tradeNumber}
                </span>
                <span
                  className={`badge border ${
                    trade.direction === TradingStrategyTradeDirection.Short
                      ? "text-bg-secondary"
                      : "text-bg-light"
                  }`}
                >
                  {tradeDirectionLabel(trade.direction)}
                </span>
                <span
                  className={`ms-auto fw-semibold fs-5 ${
                    trade.returnPct < 0 ? "text-danger" : "text-success"
                  }`}
                  style={TABULAR}
                >
                  {trade.returnValue}
                </span>
              </div>

              <dl className="mb-0 small">
                <TradeLeg
                  caption="Entry"
                  date={trade.entryDate}
                  price={trade.entryPrice}
                  decimals={priceDecimals}
                />
                <TradeLeg
                  caption="Exit"
                  date={trade.exitDate}
                  price={trade.exitPrice}
                  decimals={priceDecimals}
                />
              </dl>
            </div>
          </li>
        ))}
      </ul>

      {remaining > 0 && (
        <div className="d-grid mt-3">
          <CButton
            className="trade-show-more"
            color="secondary"
            variant="outline"
            onClick={() => setVisible((n) => n + PAGE_SIZE)}
          >
            {/* The remaining count only earns its place while it differs from the step — on the
                last press "Show 18 more (18 left)" says the same thing twice. */}
            {remaining > PAGE_SIZE
              ? `Show ${PAGE_SIZE} more (${remaining.toLocaleString()} left)`
              : `Show ${remaining.toLocaleString()} more`}
          </CButton>
        </div>
      )}
    </>
  );
}

/** One side of a trade: when it happened and at what price, kept on one line. */
function TradeLeg({
  caption,
  date,
  price,
  decimals,
}: {
  caption: string;
  date: string;
  price: number | null | undefined;
  decimals: number;
}) {
  return (
    <div className="d-flex align-items-baseline gap-2">
      <dt
        className="text-body-secondary text-uppercase fw-normal"
        style={{ fontSize: "0.7rem", letterSpacing: "0.08em", minWidth: "2.75rem" }}
      >
        {caption}
      </dt>
      <dd className="mb-0 d-flex justify-content-between flex-grow-1 gap-2" style={TABULAR}>
        <span>{formatDate(date)}</span>
        <span>{formatPrice(price, decimals)}</span>
      </dd>
    </div>
  );
}

/** A named row of pills. The caption is what stops two "All" pills reading as the same control. */
function FilterGroup({
  caption,
  children,
}: {
  caption: string;
  children: ReactNode;
}) {
  return (
    <div className="d-flex flex-wrap gap-2 trade-filter-group">
      <span
        className="text-body-secondary text-uppercase"
        style={{ fontSize: "0.7rem", letterSpacing: "0.08em", fontWeight: 600 }}
      >
        {caption}
      </span>
      {/* The pills get their own row container so a phone can put the caption above them
          without also stacking them one per line. */}
      <div className="d-flex align-items-center flex-wrap gap-2">{children}</div>
    </div>
  );
}

/**
 * Matches the pills on My Subscriptions, counts included — the count is what turns the control into
 * an answer, so "how many of these lost?" is read rather than clicked.
 */
function FilterPill({
  label,
  count,
  active,
  activeColor = "primary",
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  activeColor?: string;
  onClick: () => void;
}) {
  return (
    <CButton
      color={active ? activeColor : "secondary"}
      variant={active ? undefined : "outline"}
      size="sm"
      shape="rounded-pill"
      onClick={onClick}
    >
      {label}
      <span className="ms-1 opacity-75">({count.toLocaleString()})</span>
    </CButton>
  );
}

function decimalsFor(trades: TradingStrategyTradeDto[]): number {
  let most = 2;

  trades.forEach((trade) => {
    [trade.entryPrice, trade.exitPrice].forEach((price) => {
      if (price == null) return;

      const fraction = String(price).split(".")[1];
      if (fraction && fraction.length > most) {
        most = Math.min(fraction.length, 8);
      }
    });
  });

  return most;
}

// An export that states no price still lists the trade.
function formatPrice(
  price: number | null | undefined,
  decimals: number,
): string {
  if (price == null) return "—";

  return price.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * A daily export states no time, so midnight reads as a date rather than "00:00". Taken off the
 * string, not a Date: re-rendering in the viewer's zone would slide a trade into the previous day.
 */
function formatDate(timestamp: string): string {
  const [day, clock] = timestamp.split("T");
  if (!clock) return timestamp.slice(0, 10);

  return clock.startsWith("00:00:00") ? day : `${day} ${clock.slice(0, 5)}`;
}
